/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../../../../utils/theme-context';
import { nowRome } from '../../../../utils/date';

interface TripDatePickerProps {
  label: string;
  labelStyle?: string;
  containerStyle?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  showTime?: boolean;
  disabled?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  placeholderText?: string;
}

const CalendarIcon = ({ dark }: { dark: boolean }) => (
  <img
    src={
      dark
        ? '/assets/insights-tab/calendar-white.svg'
        : '/assets/insights-tab/calendar.svg'
    }
    className='object-contain w-[20px] pointer-events-none shrink-0'
    alt=''
  />
);

const ClockIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2' />
    <path d='M12 7v5l3 3' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const formatDisplay = (date: Date | null, showTime: boolean): string => {
  if (!date) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const m = months[date.getMonth()];
  const d = date.getDate();
  const y = date.getFullYear();
  if (!showTime) return `${m} ${d}, ${y}`;
  const hours = date.getHours();
  const mins = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${m} ${d}, ${y}  ${h12}:${mins} ${ampm}`;
};

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

const TripDatePicker = ({
  label,
  labelStyle,
  containerStyle,
  value,
  onChange,
  showTime = false,
  disabled = false,
  minDate,
  maxDate,
  placeholderText = 'Select date',
}: TripDatePickerProps) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const openPicker = () => {
    if (disabled) return;
    if (!isOpen && !value) {
      const today = nowRome();
      if (showTime) {
        today.setHours(0, 0, 0, 0);
      }
      const tooEarly = minDate && today < minDate;
      const tooLate = maxDate && today > maxDate;
      if (!tooEarly && !tooLate) {
        onChange(today);
      } else if (tooEarly && minDate) {
        onChange(new Date(minDate));
      } else if (tooLate && maxDate) {
        onChange(new Date(maxDate));
      }
    }
    setIsOpen((p) => !p);
  };

  const currentHours = value ? value.getHours() : 0;
  const currentH12 = currentHours % 12 || 12;
  const currentMins = value ? value.getMinutes() : 0;
  const currentAmPm: 'AM' | 'PM' = currentHours >= 12 ? 'PM' : 'AM';

  const [hourInput, setHourInput] = useState(currentH12.toString());
  const [minInput, setMinInput] = useState(currentMins.toString().padStart(2, '0'));
  const [amPm, setAmPm] = useState<'AM' | 'PM'>(currentAmPm);

  useEffect(() => {
    if (value) {
      const h = value.getHours();
      setHourInput((h % 12 || 12).toString());
      setMinInput(value.getMinutes().toString().padStart(2, '0'));
      setAmPm(h >= 12 ? 'PM' : 'AM');
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const applyTime = (date: Date, h12: number, mins: number, period: 'AM' | 'PM'): Date => {
    const updated = new Date(date);
    let h24 = h12 % 12;
    if (period === 'PM') h24 += 12;
    updated.setHours(h24, mins, 0, 0);
    return updated;
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) return;
    if (showTime) {
      const h = parseInt(hourInput, 10) || 12;
      const m = parseInt(minInput, 10) || 0;
      onChange(applyTime(date, clamp(h, 1, 12), clamp(m, 0, 59), amPm));
    } else {
      onChange(date);
      setIsOpen(false);
    }
  };

  const commitTime = (h12Str: string, minStr: string, period: 'AM' | 'PM') => {
    if (!value) return;
    const h = clamp(parseInt(h12Str, 10) || 12, 1, 12);
    const m = clamp(parseInt(minStr, 10) || 0, 0, 59);
    onChange(applyTime(value, h, m, period));
  };

  const onHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHourInput(raw);
  };

  const onHourBlur = () => {
    const parsed = clamp(parseInt(hourInput, 10) || 12, 1, 12);
    setHourInput(parsed.toString());
    commitTime(parsed.toString(), minInput, amPm);
  };

  const onMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMinInput(raw);
  };

  const onMinBlur = () => {
    const parsed = clamp(parseInt(minInput, 10) || 0, 0, 59);
    setMinInput(parsed.toString().padStart(2, '0'));
    commitTime(hourInput, parsed.toString(), amPm);
  };

  const displayValue = formatDisplay(value, showTime);

  const inputClass = `w-[32px] text-center text-[13px] font-inter font-medium py-1 rounded-md border outline-none transition-colors
    ${isDark
      ? 'bg-[#1E2433] border-[#374151] text-white focus:border-[#2D8EFF]'
      : 'bg-white border-[#E4E7EC] text-[#0A0D12] focus:border-[#2D8EFF]'}`;

  const amPmBtnClass = (active: boolean) =>
    `px-2 py-1 text-[11px] font-inter font-semibold rounded-md transition-all ${
      active
        ? 'bg-[#2D8EFF] text-white shadow-sm'
        : isDark
          ? 'bg-[#1E2433] text-[#A4A7AE] hover:bg-[#252B37]'
          : 'bg-[#F5F5F5] text-[#717680] hover:bg-[#E9EAEB]'
    }`;

  return (
    <div className={`relative w-full ${containerStyle || ''} ${isDark ? 'trip-datepicker-dark' : 'trip-datepicker-light'}`}>
      <label className={`block mb-1 font-medium dark:text-white ${labelStyle || ''}`}>
        {label}
      </label>

      <div
        ref={triggerRef}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') openPicker();
        }}
        role='button'
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border dark:border-[#374151] rounded bg-white dark:bg-black dark:text-white text-[13px] font-inter transition-colors
          ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-[#2D8EFF] dark:hover:border-[#2D8EFF]'}
          ${isOpen ? 'border-[#2D8EFF] dark:border-[#2D8EFF] ring-1 ring-[#2D8EFF]/20' : ''}
        `}
      >
        <span className={displayValue ? '' : 'text-[#A4A7AE]'}>
          {displayValue || placeholderText}
        </span>
        <CalendarIcon dark={isDark} />
      </div>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-[9999] mt-1 left-0 rounded-xl border shadow-lg
            ${isDark ? 'bg-[#111111] border-[#374151]' : 'bg-white border-[#E4E7EC]'}
          `}
          style={{ boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.08)' }}
        >
          <DatePicker
            selected={value}
            onChange={handleDateChange}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            inline
            calendarClassName={`trip-datepicker-calendar ${isDark ? 'trip-datepicker-calendar--dark' : ''}`}
          />

          {showTime && (
            <div className={`flex items-center justify-between gap-3 px-4 py-3 border-t ${isDark ? 'border-[#252B37]' : 'border-[#F2F4F7]'}`}>
              <div className='flex items-center gap-2'>
                <span className={`${isDark ? 'text-[#A4A7AE]' : 'text-[#717680]'}`}>
                  <ClockIcon />
                </span>
                <input
                  type='text'
                  inputMode='numeric'
                  value={hourInput}
                  onChange={onHourChange}
                  onBlur={onHourBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className={inputClass}
                  placeholder='12'
                />
                <span className={`text-[15px] font-semibold ${isDark ? 'text-[#A4A7AE]' : 'text-[#717680]'}`}>:</span>
                <input
                  type='text'
                  inputMode='numeric'
                  value={minInput}
                  onChange={onMinChange}
                  onBlur={onMinBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className={inputClass}
                  placeholder='00'
                />
                <div className='flex gap-0.5 ml-1'>
                  <button type='button' onClick={() => { setAmPm('AM'); commitTime(hourInput, minInput, 'AM'); }} className={amPmBtnClass(amPm === 'AM')}>AM</button>
                  <button type='button' onClick={() => { setAmPm('PM'); commitTime(hourInput, minInput, 'PM'); }} className={amPmBtnClass(amPm === 'PM')}>PM</button>
                </div>
              </div>

              <button
                type='button'
                onClick={() => setIsOpen(false)}
                className='text-[12px] font-inter font-medium px-4 py-1.5 rounded-md bg-[#2D8EFF] text-white hover:bg-[#1a7ae6] transition-colors'
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TripDatePicker;
